import React, {
  useEffect,
  useRef,
} from 'react';
import {
  useDispatch, useSelector, useStore, batch,
} from 'react-redux';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import Helmet from 'react-helmet';
import { Global, css } from '@emotion/core';
import Layout from '../../shared/components/Layout/Layout';
import { CalendarModalHandler, SecondLayerModal } from './CalendarModalHandler';
import {
  fetchAllExercisesForAccount,
  fetchAthletes,
  fetchTagsForAccount,
  fetchQuestionsForAccount,
  fetchParentCalendars,
  fetchSavedWorkouts,
  fetchAndSetDefaultCalendar,
  fetchAndSetUrlAthlete,
  fetchAndSetUrlCalendar,
  fetchAndSetUrlProgram,
  setSelectedDate,
  copySelectedItemsToClipboard,
  pasteClipboardItems,
  setActiveModal,
} from './ducks/calendarActions';
import CalendarLayout from './CalendarLayout';
import EntryPointContent from './components/presentational/EntryPointContent';

const Calendar = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const selectedReduxCalendar = useSelector((state) => state.calendar.data.selectedReduxCalendar);
  const selectedReduxAthlete = useSelector((state) => state.calendar.data.selectedReduxAthlete);
  const accountCode = currentUser?.accountCode;
  const programType = useSelector((state) => state.calendar.data.programType);
  const selectedReduxNonDateBasedProgram = useSelector(
    (state) => state.calendar.data.selectedReduxNonDateBasedProgram,
  );

  const lastHandledPathname = useRef(null);
  const viewType = useSelector((state) => state.calendar.data.viewType);
  const store = useStore();
  const copiedSelectedItems = useSelector((state) => state.calendar.data.copiedSelectedItems);

  // Load ContentSquare heatmap script
  useEffect(() => {
    // Prevent duplicate injection
    if (document.querySelector('script[data-heatmap="true"]')) return;

    const script = document.createElement('script');
    script.src = 'https://t.contentsquare.net/uxa/91be1269c2b06.js';
    script.async = true;
    script.dataset.heatmap = 'true';

    document.head.appendChild(script);
  }, []);

  // Parse URL query params for deeplink targets:
  //   /build?id=A{userId}&date={YYYY-MM-DD}  — athlete calendar
  //   /build?id=C{calendarId}&date={YYYY-MM-DD}  — calendar (date optional)
  //   /build?id=P{programId}  — program
  useEffect(() => {
    if (!accountCode || location.search === lastHandledPathname.current) return;

    const params = new URLSearchParams(location.search);
    const id = params.get('id');
    const dateString = params.get('date');

    if (!id) return;

    const prefix = id.charAt(0);
    const numericId = id.substring(1);

    if (!/^\d+$/.test(numericId)) return;

    if (prefix === 'A' && dateString && moment(dateString, 'YYYY-MM-DD', true).isValid()) {
      dispatch(setSelectedDate(moment(dateString)));
      dispatch(fetchAndSetUrlAthlete(accountCode, parseInt(numericId, 10)));
      lastHandledPathname.current = location.search;
    } else if (prefix === 'C') {
      if (dateString && moment(dateString, 'YYYY-MM-DD', true).isValid()) {
        dispatch(setSelectedDate(moment(dateString)));
      }
      dispatch(fetchAndSetUrlCalendar(accountCode, parseInt(numericId, 10)));
      lastHandledPathname.current = location.search;
    } else if (prefix === 'P') {
      dispatch(fetchAndSetUrlProgram(accountCode, parseInt(numericId, 10)));
      lastHandledPathname.current = location.search;
    }
  }, [accountCode, location.search]);

  useEffect(() => {
    /** fetch all exercises associated with all types for side bar */
    if (accountCode) {
      batch(() => {
        dispatch(fetchAllExercisesForAccount(accountCode));
        dispatch(fetchAthletes(accountCode));
        dispatch(fetchTagsForAccount(accountCode));
        dispatch(fetchQuestionsForAccount(accountCode));
        dispatch(fetchParentCalendars(accountCode));
        dispatch(fetchSavedWorkouts(accountCode));
      });
    }
  }, [accountCode]);

  useEffect(() => {
    // Check if URL contains a deeplink id param to prevent loading default calendar
    const params = new URLSearchParams(location.search);
    const hasDeeplinkInUrl = !!params.get('id');

    if (currentUser && Object.keys(currentUser).length > 0 && currentUser.defaultCalendar) {
      // Only fetch default calendar if no calendar is currently selected AND no deeplink in URL
      // This prevents overriding a newly created calendar or URL-specified athlete/calendar/program
      if (!hasDeeplinkInUrl && (!selectedReduxCalendar || Object.keys(selectedReduxCalendar).length === 0) && (!selectedReduxAthlete || Object.keys(selectedReduxAthlete).length === 0) && programType === 'calendar') {
        dispatch(fetchAndSetDefaultCalendar(currentUser));
      }
    }
  }, [currentUser, selectedReduxCalendar, location.search]);

  // Keyboard shortcuts for copy/paste/delete
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't intercept when typing in inputs
      const tag = e.target.tagName;
      if ((tag === 'INPUT' && e.target.type !== 'checkbox') || tag === 'TEXTAREA' || e.target.isContentEditable) {
        return;
      }

      // Check for Command (Mac) or Ctrl (Windows/Linux)
      const modifierKey = e.metaKey || e.ctrlKey;

      if (modifierKey && e.key.toLowerCase() === 'p') {
        e.preventDefault();
      }

      if (modifierKey && e.key === 'c') {
        // Only hijack native copy if there are selected calendar items
        const currentSelected = store.getState().calendar.data.selectedItems;
        const hasSelectedItems = currentSelected
          && Object.keys(currentSelected).length > 0;
        if (hasSelectedItems) {
          e.preventDefault();
          dispatch(copySelectedItemsToClipboard());
        }
      }
      if (modifierKey && e.key === 'v') {
        // Only hijack native paste if there are copied items to paste
        if (copiedSelectedItems) {
          e.preventDefault();
          dispatch(pasteClipboardItems());
        }
      }
      if (modifierKey && e.key === 'd') {
        const state = store.getState().calendar.data;
        const currentSelected = state.selectedItems;
        const hasSelectedItems = currentSelected
          && Object.keys(currentSelected).length > 0;
        if (hasSelectedItems) {
          e.preventDefault();
          e.stopPropagation();
          const selectedItemArray = Object.values(currentSelected);
          const preSaveItems = selectedItemArray.filter((item) => item.preSave);
          const nonPreSaveItems = selectedItemArray.filter((item) => !item.preSave);
          const {
            programType: currentProgramType,
            selectedDate,
            selectedProgramDayNumber,
            viewType: currentViewType,
            weekViewExpandedDay,
          } = state;

          dispatch(setActiveModal('confirmDeleteModal', {
            isBulkDelete: true,
            bulkDeleteAssignedIds: nonPreSaveItems.map((item) => item.assignedId),
            ...(currentProgramType === 'calendar' && {
              date: moment(
                currentViewType === 'weekView' ? weekViewExpandedDay : selectedDate,
              ).format('YYYY-MM-DD'),
            }),
            ...(currentProgramType === 'free-form-program' && { dayNumber: selectedProgramDayNumber }),
            preSaveItems,
          }));
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [dispatch, store, copiedSelectedItems]);

  return (
    <Layout fullWidth overflowVisible={viewType === 'weekView'}>
      {viewType === 'weekView' && (
      <Global
        styles={css`
        body, html {
          overflow: visible;
          overscroll-behavior: none;
          /* Hide scrollbar for WebKit browsers */
          &::-webkit-scrollbar {
            display: none;
          }

          /* Hide scrollbar for Firefox */
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        html, body, #app, #app > div {
          overflow: visible;
          overscroll-behavior: none;
          /* Hide scrollbar for WebKit browsers */
          &::-webkit-scrollbar {
            display: none;
          }

          /* Hide scrollbar for Firefox */
          scrollbar-width: none;
          -ms-overflow-style: none;
          }
      `}
      />
      )}
      <Helmet>
        <title>Calendar | TeamBuildr</title>
      </Helmet>
      {/* <SupersetOverlay /> */}
      <CalendarModalHandler />
      <SecondLayerModal />
      {!Object.keys(selectedReduxCalendar).length
  && !Object.keys(selectedReduxAthlete).length
  && !Object.keys(selectedReduxNonDateBasedProgram).length
        ? <EntryPointContent /> : <CalendarLayout />}
    </Layout>
  );
};

Calendar.propTypes = { };
export default Calendar;
